import React, { useState, useEffect } from "react";
import { Term } from "../../../../services/Dtos";
import { VStack, Box } from "@chakra-ui/react";
import CustomAlert from "../../../CustomUi/CustomAlert";
import CustomModal from "../../../CustomUi/CustomModal";
import TextInput from "../../../CustomUi/TextInput";
import CustomNumberInput from "../../../CustomUi/CustomNumberInput";
import useIcons from "../../../../hooks/useIcons";
import { useConfirmDialog } from "../../../../hooks/useConfirmDialog";
import { useFormChanges } from "../../../../hooks/useFormChanges";
import { useFormFieldHandler } from "../../../../hooks/useFormFieldHandler";
import { useTermService } from "../../../../hooks/Domain/useTermService";
import InlineSeparator from "../../../CustomUi/InlineSeparator";
import Info from "../../../CustomUi/Info";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  termToEdit: Term | null;
  nextOrder: number;
}

const TermModal: React.FC<Props> = ({
  isOpen,
  onClose,
  termToEdit,
  nextOrder,
}) => {
  const {
    createTerm,
    updateTerm,
    deleteTerm,
    openDeleteDialog,
    setOpenDeleteDialog,
    errors,
    clearErrors,
    isLoading,
  } = useTermService();

  const getInitialTermFormState = (): Term => {
    return {
      id: "",
      termName: "",
      isLeapTerm: false,
      schoolId: "",
      order: nextOrder,
      createdAt: "",
      updatedAt: "",
    };
  };

  const initialTermFormState = getInitialTermFormState();
  const icons = useIcons();
  const [termFormState, setTermFormState] =
    useState<Term>(initialTermFormState);
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());

  // Initialize confirmation dialog
  const { confirmUnsavedChanges } = useConfirmDialog();

  // Define field configurations for form validation
  const fieldConfigs = {
    termName: { type: "text" as const, required: true, maxLength: 100 },
    order: { type: "number" as const, required: true },
  };

  // Use the form changes hook
  const { hasChanges, validateField, validateAllFields, hasValidationErrors } =
    useFormChanges({
      fields: fieldConfigs,
      initialData: termToEdit,
      currentData: termFormState,
      isEditMode: !!termToEdit,
    });

  // Use the form field handler hook
  const { handleFieldChange } = useFormFieldHandler({
    setFormState: setTermFormState,
    setTouchedFields,
    integerFields: ["order"],
  });

  // Real-time validation using the hook's validateField
  const validateFieldByName = (name: string, value: any): string => {
    const fieldKey = name as keyof Term;
    return validateField(fieldKey, value);
  };

  // Clear errors when modal opens
  useEffect(() => {
    if (isOpen) {
      clearErrors();
      setTouchedFields(new Set());
    }
  }, [isOpen, clearErrors]);

  useEffect(() => {
    if (termToEdit) {
      setTermFormState(termToEdit);
    } else {
      setTermFormState(getInitialTermFormState());
    }
    setTouchedFields(new Set());
  }, [termToEdit, isOpen]);

  const showFieldError = (fieldName: string): boolean => {
    const error = validateFieldByName(
      fieldName,
      termFormState[fieldName as keyof Term]
    );
    return !!(error && touchedFields.has(fieldName));
  };

  const getFieldError = (fieldName: string): string => {
    return validateFieldByName(
      fieldName,
      termFormState[fieldName as keyof Term]
    );
  };

  const currentValidationErrors = validateAllFields();
  const hasValidationErrorsValue = hasValidationErrors(currentValidationErrors);

  const handleSave = async () => {
    if (hasValidationErrorsValue || !hasChanges) return;

    if (termToEdit) {
      const response = await updateTerm(termToEdit.id, termFormState);
      if (response.result.succeeded) {
        onClose();
      }
    } else {
      const response = await createTerm(termFormState);
      if (response.result.succeeded) {
        onClose();
      }
    }
  };

  const handleDeleteTerm = async () => {
    if (!termToEdit) return;

    const response = await deleteTerm(termToEdit.id);
    if (response.result.succeeded) {
      onClose();
    }
  };

  const handleClose = async () => {
    if (hasChanges && termToEdit) {
      const confirmed = await confirmUnsavedChanges();
      if (confirmed) {
        clearErrors();
        onClose();
      }
    } else {
      clearErrors();
      onClose();
    }
  };

  return (
    <>
      <CustomModal
        isOpen={isOpen}
        onClose={handleClose}
        handleClose={handleClose}
        handleAction={handleSave}
        handleDelete={handleDeleteTerm}
        deleteMessage="All related term records and student assignments will be lost permanently"
        isLoading={isLoading}
        hasChanges={hasChanges}
        hasValidationErrors={hasValidationErrorsValue}
        isDisabled={!hasChanges || hasValidationErrorsValue || isLoading}
        title={termToEdit ? "Edit Term" : "Add New Term"}
        icon={icons.main.term}
        mode={termToEdit ? "edit" : "add"}
        openDeleteDialog={openDeleteDialog}
        setOpenDeleteDialog={setOpenDeleteDialog}
        actionText={termToEdit ? "Save Changes" : "Save Term"}
      >
        {errors.length > 0 && (
          <VStack gap={2} mb={4} align={"stretch"}>
            {errors.slice(0, 3).map((error, index) => (
              <CustomAlert key={index} message={error} tight />
            ))}
          </VStack>
        )}

        <VStack gap={6} align="stretch">
          <Box>
            <InlineSeparator label="Term Information" />

            <VStack gap={4}>
              <TextInput
                label="Term Name"
                name="termName"
                type="text"
                value={termFormState.termName}
                onChange={handleFieldChange}
                placeholder="e.g (1st Term - 2020/2021)"
                required
                error={
                  showFieldError("termName") ? getFieldError("termName") : ""
                }
                invalid={showFieldError("termName")}
              />

              <CustomNumberInput
                label="Order"
                value={termFormState.order.toString()}
                onChange={(value) => {
                  handleFieldChange({
                    target: { name: "order", value },
                  } as any);
                }}
                placeholder="Position of this term"
                min={1}
                error={showFieldError("order") ? getFieldError("order") : ""}
                invalid={showFieldError("order")}
                info={
                  <Info content="The order of the term in a list of terms. This helps with arranging the terms the way you want." />
                }
              />
            </VStack>
          </Box>
        </VStack>
      </CustomModal>
    </>
  );
};

export default TermModal;
